import React, { useState } from 'react';
import { claimLiteValuation } from '../../services/api';
import './EmailGate.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function safeDevLink(raw) {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if ((url.protocol === 'http:' || url.protocol === 'https:') && url.pathname === '/auth/verify') {
      return url.href;
    }
  } catch (e) { /* fall through */ }
  return null;
}

/**
 * EmailGate — post-wizard email capture component.
 *
 * Props:
 *   valuationId — the valuation ID returned by the wizard
 *
 * States:
 *   capture — headline, email input, submit button
 *   sent    — confirmation with email shown + optional dev link
 */
function EmailGate({ valuationId }) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [sentState, setSentState] = useState(null); // { email, devLink? }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError(null);
    setApiError(null);

    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      const data = await claimLiteValuation(valuationId, email);
      setSentState({ email, devLink: safeDevLink(data.devLink) });
    } catch (_) {
      setApiError('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setSentState(null);
    setEmail('');
    setEmailError(null);
    setApiError(null);
  };

  if (sentState) {
    return (
      <div className="email-gate email-gate--sent">
        <h2 className="email-gate-heading">Check your inbox</h2>
        <p className="email-gate-sent-email">{sentState.email}</p>
        <p className="email-gate-expiry">The link expires in 30 minutes.</p>
        <p className="email-gate-retry-hint">
          Didn't get it? Check spam or try again.
        </p>
        <button
          type="button"
          className="email-gate-link-btn"
          onClick={handleUseDifferentEmail}
        >
          Use a different email
        </button>
        {sentState.devLink && (
          <a
            data-testid="dev-link"
            href={sentState.devLink}
            className="email-gate-dev-link"
          >
            Dev: open magic link
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="email-gate email-gate--capture">
      <h2 className="email-gate-heading">Your valuation is ready</h2>
      <p className="email-gate-sub">
        Enter your email and we'll send you a secure link to view it.
      </p>

      {apiError && (
        <div className="email-gate-error-banner" role="alert">
          {apiError}
        </div>
      )}

      <form className="email-gate-form" onSubmit={handleSubmit} noValidate>
        <div className="email-gate-field">
          <label htmlFor="eg-email">Email address</label>
          <input
            type="email"
            id="eg-email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            placeholder="you@example.com"
            disabled={submitting}
            className={emailError ? 'email-gate-input--error' : ''}
          />
          {emailError && (
            <span className="email-gate-field-error">{emailError}</span>
          )}
        </div>

        <button
          type="submit"
          className="email-gate-btn"
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Email me my valuation'}
        </button>
      </form>
    </div>
  );
}

export default EmailGate;
